import Cookies from 'js-cookie';
import cn from 'classnames';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import { useLocale } from '@hooks/useLocale';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import LoginFields from '../LoginFields';
import ResetPasswordAlert from '../ResetPasswordAlert';

import { AccountTabs } from '@components/account/types';
import AlertInlineMessage from '@components/ui/AlertInlineMessage';
import Button from '@components/ui/Button';
import LoadingSpinner from '@components/ui/LoadingSpinner';

import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';

import {
  FEATURE_FLAG_GUEST_CHECKOUT,
  FEATURE_FLAG_LOYALTY,
  loyaltyFFCookieName,
} from '@config/feature-flag';

import { useUI } from '@context/GlobalUIContext';

import useLoginForm from '@hooks/useLoginForm';

import { useCheckEmail } from '@adapters/bigcommerce/hooks/customer';
import { trackSignInEvent } from '@utils/gtm/ga4/trackSignInEvent';

import styles from './LoginForm.module.scss';

interface LoginFormProps {
  onLogin?: (customerGroupId?: number) => void;
  destination?: 'checkout' | AccountTabs.MemberRewards | 'default';
  isModal?: boolean;
}

const LoginForm = ({
  onLogin,
  destination = 'default',
  isModal = false,
}: LoginFormProps) => {
  const t = useTranslationWithSelfFallback('common');
  const tCheckout = useTranslationWithSelfFallback('checkout');
  const {
    email,
    password,
    errorMessage,
    onSubmit,
    isLoading,
    isDisabled,
    isDirty,
  } = useLoginForm(onLogin);
  const { displayLoginModal } = useUI();
  const locale = useLocale();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const checkEmail = useCheckEmail();
  const [showResetPasswordAlert, setShowResetPasswordAlert] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  useEffect(() => {
    if (!displayLoginModal && showResetPasswordAlert) {
      setShowResetPasswordAlert(false);
    }
  }, [displayLoginModal, showResetPasswordAlert]);

  useEffect(() => {
    setDisableButton(false);
  }, [email.value, password.value]);

  const onSubmitWithReCAPTCHA = async (
    e: React.SyntheticEvent<EventTarget>,
    loginButtonText: string
  ) => {
    e.preventDefault();
    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    recaptchaRef.current?.reset();
    const res = await checkEmail(email.value, recaptchaToken);
    if (res?.passwordReset?.force_password_reset) {
      setDisableButton(true);
      setShowResetPasswordAlert(true);
    } else if (errorMessage) {
      setShowResetPasswordAlert(false);
    }
    if (isModal) {
      trackSignInEvent({
        interactionLocation: 'sign in modal',
        linkText: loginButtonText,
      });
    }
    await onSubmit(recaptchaRef?.current, e);
  };

  const [loyaltyFeatureFlag, setLoyaltyFeatureFlag] =
    useState(FEATURE_FLAG_LOYALTY);
  useLayoutEffect(() => {
    const loyaltyCookie = Cookies.get(loyaltyFFCookieName);
    if (loyaltyCookie === 'true') {
      setLoyaltyFeatureFlag(true);
    }
  }, []);

  const loginButtonText =
    destination === 'checkout'
      ? tCheckout('checkout.sign.in.button')
      : destination === AccountTabs.MemberRewards && loyaltyFeatureFlag
      ? t('login.sign.in.loyalty')
      : t('login.sign.in');
  const createAccountLinkURL =
    destination === 'checkout'
      ? `/${locale}/auth/register?redirect=checkout`
      : `/${locale}/auth/register`;

  return (
    <>
      {showResetPasswordAlert && <ResetPasswordAlert isModal />}
      {!showResetPasswordAlert && errorMessage && (
        <AlertInlineMessage
          classNames={styles.loginErrorMessage}
          messageType="ERROR"
        >
          {errorMessage}
        </AlertInlineMessage>
      )}
      <form
        onSubmit={(e) => onSubmitWithReCAPTCHA(e, loginButtonText)}
        className={styles.formContainer}
      >
        <LoginFields email={email} password={password} isDirty={isDirty} />
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          badge="bottomleft"
          sitekey={`${process.env.NEXT_PUBLIC_RECAPTCHA_KEY}`}
        />
        <Button
          variant="primary"
          type="submit"
          disabled={isDisabled || isLoading || disableButton}
          label={
            isLoading ? (
              <LoadingSpinner
                classNames={styles.loadingSpinner}
                colour="var(--color-white)"
              />
            ) : (
              loginButtonText
            )
          }
          size="large"
          className={cn(styles.loginButton, {
            [styles.isDisabled]: isLoading,
          })}
          dataTestid="login-button"
        />
        {!FEATURE_FLAG_GUEST_CHECKOUT && (
          <Button
            href={createAccountLinkURL}
            variant="secondary"
            label={t('login.create.account')}
            size="large"
            className={styles.createAccountButton}
          />
        )}
      </form>
    </>
  );
};

export default LoginForm;
